/**
 * LoopShell
 *
 * Page layout for the `loop-full` theme: persistent right-side sidebar + main area.
 * In RTL the sidebar sits on the natural-start side (right). Implemented with
 * CSS grid using `grid-template-columns: 1fr 240px` so the sidebar is fixed-width
 * on the trailing visual side when the document direction is RTL.
 *
 * On mobile (< 768px): sidebar is hidden, a top hamburger bar appears, and the
 * sidebar slides in as a full-screen overlay when toggled.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import LoopSidebar from './LoopSidebar';
import MobileHeader from './MobileHeader';

interface LoopShellProps {
  children: ReactNode;
}

const LoopShell = ({ children }: LoopShellProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = useCallback(() => setMobileMenuOpen((v) => !v), []);
  const closeMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [mobileMenuOpen]);

  return (
    <div className="loop-full-shell">
      <MobileHeader onMenuToggle={toggleMenu} />

      {mobileMenuOpen && (
        <div
          className="loop-mobile-backdrop"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      <div className={`loop-sidebar-wrapper ${mobileMenuOpen ? 'is-open' : ''}`}>
        <LoopSidebar onNavigate={closeMenu} />
      </div>

      <main className="loop-full-shell__main">{children}</main>
    </div>
  );
};

export default LoopShell;
